import { DISPLAY_CONTENT, TOAST_ACTIONS } from 'modules/globalSms/constants';
import { useMutationUpdateSmsPreferences } from 'modules/globalSms/hooks';
import { EditablePreferenceFields, OptOutType } from 'modules/globalSms/types';
import { isUnitedStatesOrCanada } from 'modules/globalSms/utils';
import { SmsCountryPreferences } from 'modules/graphql';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { MouseEvent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, Button } from 'ui/button';
import {
  ConfirmationPrompt,
  MODAL_SIZES,
  MODAL_VARIANTS,
  Modal,
  ModalBody,
  ModalConfirmationFooter,
  ModalHeader,
} from 'ui/modal';
import { selectIsBossOrSalesBossLevel } from 'core/userContext';

import { FrequencyFormField } from './FrequencyFormField';
import { OptOutTextFormField } from './OptOutTextFormField';
import { StyledForm, StyledWarningBanner } from './styles';

const {
  headerAppend,
  headerCountryFallback,
  saveButtonLabel,
  toastUpdateErrorMessageAppend,
  toastUpdateErrorMessagePrepend,
  toastSuccessMessageAppend,
  warningBannerText,
} = DISPLAY_CONTENT.editModal;

const ERROR_TOAST_TIMEOUT = 5000;

type Props = Pick<
  SmsCountryPreferences,
  'regionCode' | 'regionName' | 'alphanumericOptOut' | 'numericOptOut' | 'frequency'
> & {
  isModalOpen: boolean;
  setIsModalOpen: (val: boolean) => void;
};

export const EditPreferencesModal = ({
  regionCode,
  regionName,
  alphanumericOptOut,
  numericOptOut,
  frequency,
  isModalOpen,
  setIsModalOpen,
}: Props) => {
  const isBossUser = useSelector(selectIsBossOrSalesBossLevel);
  const dispatch = useDispatch();

  const { updateSmsPreferences } = useMutationUpdateSmsPreferences(
    () => {
      setIsModalOpen(false);
      dispatch(addPositiveToast(`${regionName} ${toastSuccessMessageAppend}`));
    },
    (e) => {
      dispatch(
        addNegativeToastWithErrorId(
          `${toastUpdateErrorMessagePrepend} ${regionName}${toastUpdateErrorMessageAppend}`,
          TOAST_ACTIONS.updateSmsPreferences,
          e,
          { timeout: ERROR_TOAST_TIMEOUT },
        ),
      );
    },
  );

  const formMethods = useForm<EditablePreferenceFields>({
    defaultValues: {
      alphanumericOptOut,
      numericOptOut,
      frequency,
    },
  });
  const {
    handleSubmit,
    formState: { dirtyFields, isDirty },
  } = formMethods;
  const isOptOutTextDirty = Object.keys(dirtyFields).some(
    (key) => key === 'alphanumericOptOut' || key === 'numericOptOut',
  );

  const [confirmationPromptType, setConfirmationPromptType] = useState<ConfirmationPrompt>(
    ConfirmationPrompt.NONE,
  );

  const handleCancel = (e?: MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (isDirty) {
      setConfirmationPromptType(ConfirmationPrompt.DISCARD);
    } else {
      setIsModalOpen(false);
    }
  };

  const handleSave = (e: MouseEvent) => {
    e.preventDefault();
    setConfirmationPromptType(ConfirmationPrompt.CONFIRM);
  };

  const handleDiscard = () => setIsModalOpen(false);

  const handleResume = () => setConfirmationPromptType(ConfirmationPrompt.NONE);

  const handleConfirmSave = (editableFields: EditablePreferenceFields) => {
    updateSmsPreferences({ variables: { regionCode, ...editableFields } });
  };

  const modalId = `edit-preferences-modal-${regionCode.toLowerCase()}`;

  return (
    <Modal
      data-test="edit-preferences-modal"
      id={modalId}
      isModalOpen={isModalOpen}
      onClose={handleCancel}
      withPortal
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <FormProvider {...formMethods}>
        <StyledForm onSubmit={handleSubmit(handleConfirmSave)}>
          <ModalHeader onClose={handleCancel}>{`${
            regionName || headerCountryFallback
          } ${headerAppend}`}</ModalHeader>
          <ModalBody>
            <FrequencyFormField isDisabled={!isUnitedStatesOrCanada(regionCode) && !isBossUser} />
            {!isUnitedStatesOrCanada(regionCode) && (
              <OptOutTextFormField optOutType={OptOutType.ALPHANUMERIC} regionCode={regionCode} />
            )}
            <OptOutTextFormField optOutType={OptOutType.NUMERIC} regionCode={regionCode} />
            {isOptOutTextDirty && <StyledWarningBanner>{warningBannerText}</StyledWarningBanner>}
          </ModalBody>
          <ModalConfirmationFooter
            onCancel={handleCancel}
            onDiscard={handleDiscard}
            onResume={handleResume}
            promptType={confirmationPromptType}
          >
            <Button isDisabled={!isDirty} onClick={handleSave} size={BUTTON_SIZES.LARGE}>
              {saveButtonLabel}
            </Button>
          </ModalConfirmationFooter>
        </StyledForm>
      </FormProvider>
    </Modal>
  );
};
